import Arrow from '@/app/components/search/components/Filters/HorizontalFilters/Arrow';
import { arrowWidth } from '@/app/components/search/components/Filters/HorizontalFilters/Arrow/styled.ts';
import React, { useImperativeHandle, useRef } from 'react';
import { useIsVisible } from '@/app/common/hooks/useIsVisible';
import { getFocusableElements } from '@/app/common/hooks/useFocusTrap.ts';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { Boundary, InnerContainer, OuterContainer } from './styled.ts';

export type ScrollContainerHandle = { scrollToStart: () => void };

type ScrollContainerProps = {
  style?: React.CSSProperties;
  styleInner?: React.CSSProperties;
  children?: React.ReactNode;
  // MPH-398: opt out of the scroll-affordance arrows (the Evo filters bar scrolls by touch only)
  hideArrows?: boolean;
  pointerOnlyArrows?: boolean;
  evo?: boolean;
  ref?: React.Ref<ScrollContainerHandle>;
};

const revealKeyboardFocusedPill = ({ currentTarget, target, key }: React.KeyboardEvent<HTMLElement>) => {
  if ((key === 'Tab' || key === 'Escape') && target instanceof HTMLElement && currentTarget.contains(target)) {
    target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
};

const ScrollContainer = ({
  style,
  styleInner,
  children,
  hideArrows,
  pointerOnlyArrows,
  evo,
  ref,
}: ScrollContainerProps) => {
  const pillsContainerRef = useRef<HTMLDivElement>(null);

  // Not smooth: the re-render that reorders the pills cancels an animation mid-flight, leaving the
  // bar wherever it had got to.
  useImperativeHandle(
    ref,
    () => ({
      scrollToStart: () => pillsContainerRef.current?.scroll({ left: 0, behavior: 'instant' }),
    }),
    []
  );

  const startBoundaryRef = useRef<HTMLDivElement>(null);
  const endBoundaryRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const childrenArray = React.Children.toArray(children);
  const isStartVisible = useIsVisible(startBoundaryRef, 0.1, pillsContainerRef);
  const isEndVisible = useIsVisible(endBoundaryRef, 0.1, pillsContainerRef);

  const outerRef = useRef<HTMLDivElement>(null);

  const handleArrow = (val: string) => {
    const pills = pillsContainerRef.current;
    if (!pills) return;
    const left = pills.scrollLeft + (val === 'left' ? -100 : 100);
    pills.scroll({ left, behavior: 'smooth' });

    const start = startBoundaryRef.current;
    const end = endBoundaryRef.current;
    const reachesItsEnd =
      val === 'left'
        ? !!start && left < start.offsetLeft + start.offsetWidth
        : !!end && left + pills.clientWidth > end.offsetLeft;
    const { activeElement } = document;
    const arrowHasFocus = !pills.contains(activeElement) && outerRef.current?.contains(activeElement);
    if (reachesItsEnd && arrowHasFocus) {
      const filters = getFocusableElements(pills);
      (val === 'left' ? filters[0] : filters.at(-1))?.focus({ preventScroll: true });
    }
  };

  return (
    <OuterContainer ref={outerRef} style={style}>
      {!hideArrows && !isStartVisible && (
        <Arrow
          handleArrowClick={handleArrow}
          noShadow={false}
          evo={evo}
          pointerOnly={pointerOnlyArrows}
          aria-label={t('vacation.oss.filters.show_more')}
        />
      )}
      <InnerContainer
        ref={pillsContainerRef}
        id="pills-scrollable"
        style={styleInner}
        $scrollPadding={hideArrows ? 0 : arrowWidth(evo)}
        onKeyUp={revealKeyboardFocusedPill}
      >
        <Boundary ref={startBoundaryRef} className="start-boundary" />
        {childrenArray.map((child, index) => (
          <div
            key={React.isValidElement(child) ? child.key : index}
            className={index === childrenArray.length - 1 ? 'last' : undefined}
          >
            {child}
          </div>
        ))}
        <Boundary ref={endBoundaryRef} className="end-boundary" />
      </InnerContainer>
      {!hideArrows && !isEndVisible && (
        <Arrow
          handleArrowClick={handleArrow}
          noShadow={false}
          isRightArrow={true}
          evo={evo}
          pointerOnly={pointerOnlyArrows}
          aria-label={t('vacation.oss.filters.show_more')}
        />
      )}
    </OuterContainer>
  );
};

export default ScrollContainer;
